import {
  applyPreset,
  countSet,
  DEFAULT_GAME_UI,
  type GameUiLook,
  resolveValue,
  UI_FONT_NAMES,
  UI_FONTS,
  UI_PRESET_IDS,
  UI_PRESETS,
  UI_SIZES,
  UI_TARGETS,
  type UiColours,
  type UiCorners,
  type UiFont,
  type UiKey,
  type UiPanel,
  type UiResolved,
  type UiScreen,
  type UiSize,
  type UiTarget,
  type UiValues,
  type UiView,
  uiContrastChecks,
  viewChain,
} from '@mogzo/shared';
import { applyTheme, readTheme } from '@mogzo/ui';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  ChoiceSetting,
  CornersSetting,
  DeviceIcon,
  NumberSetting,
  type Source,
} from '../components/game-ui/controls';
import {
  CHOICE_SPECS,
  isChoice,
  NUMBER_SPECS,
  SIZE_NAMES,
  sectionsFor,
  TARGET_NAMES,
  viewName,
  withValue,
} from '../components/game-ui/model';
import { Stage } from '../components/game-ui/Stage';
import { ErrorNotice, Loading } from '../components/ui';
import { ApiError, apiSend, type Project } from '../lib/api';
import { timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';

interface SavedLook {
  look: GameUiLook;
  saved: boolean;
  updatedAt: string | null;
}

const COLOURS: [keyof UiColours, string][] = [
  ['panel', 'Panels'],
  ['text', 'Text'],
  ['muted', 'Quiet text'],
  ['accent', 'Buttons and highlights'],
  ['onAccent', 'Text on buttons'],
  ['field', 'Tiles and rows'],
  ['line', 'Lines and switches off'],
];

/** Panels a phone shows as sheets from the bottom. */
const SHEETS: UiTarget[] = ['leaderboard', 'achievements', 'pause', 'signin', 'match'];

function Svg({
  children,
  size = 18,
  width = 2,
}: {
  children: ReactNode;
  size?: number;
  width?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const TARGET_ICONS: Record<UiTarget, ReactNode> = {
  theme: (
    <>
      <circle cx="13.5" cy="6.5" r="1" />
      <circle cx="17.5" cy="10.5" r="1" />
      <circle cx="8.5" cy="7.5" r="1" />
      <circle cx="6.5" cy="12.5" r="1" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.9 0 1.7-.8 1.7-1.7 0-.4-.2-.8-.4-1.1-.3-.3-.4-.7-.4-1.1 0-.9.8-1.7 1.7-1.7H16c3.1 0 5.6-2.5 5.6-5.6C22 5.9 17.5 2 12 2z" />
    </>
  ),
  popup: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />,
  leaderboard: (
    <>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </>
  ),
  achievements: (
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  ),
  pause: (
    <>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </>
  ),
  signin: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  match: (
    <>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M15 20a4.5 4.5 0 0 1 6-4" />
    </>
  ),
};

/** Where a value comes from, in the words of the settings panel. */
function sourceOf(r: UiResolved<UiKey>, view: UiView, target: UiTarget, label: string): Source {
  const own = r.view === view && r.target === target;
  if (own) return { own, text: 'This view', title: `${label} is set for ${viewName(view)}.` };
  const theme = r.target !== target;
  const from =
    r.view === null
      ? 'MOGZO’s default'
      : `${theme ? 'Theme, ' : ''}${r.view === view ? 'this view' : viewName(r.view)}`;
  return {
    own,
    text:
      r.view === null
        ? 'MOGZO default'
        : `${theme ? 'Theme · ' : ''}${r.view === view ? 'this view' : viewName(r.view)}`,
    title: `${label} comes from ${from}. Change it to set it for ${viewName(view)} only.`,
  };
}

const same = (a: GameUiLook, b: GameUiLook) => JSON.stringify(a) === JSON.stringify(b);

/** The Game UI editor: a page builder for MOGZO's panels over the game, view by view (M19). */
function Editor({ project, initial }: { project: Project; initial: SavedLook }) {
  const [look, setLook] = useState<GameUiLook>(initial.look);
  const [history, setHistory] = useState<{ past: GameUiLook[]; future: GameUiLook[] }>({
    past: [],
    future: [],
  });
  /** What the last change was to and when, so a slider's drag is one step to undo. */
  const lastEdit = useRef({ edit: '', at: 0 });
  const [saved, setSaved] = useState({
    look: initial.look,
    at: initial.updatedAt,
    ever: initial.saved,
  });
  const [size, setSize] = useState<UiSize>('computer');
  const [screen, setScreen] = useState<UiScreen>('game');
  const [target, setTarget] = useState<UiTarget>('theme');
  const [shown, setShown] = useState<UiPanel>('leaderboard');
  const [mode, setMode] = useState<'elements' | 'settings'>('elements');
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [unlinked, setUnlinked] = useState<Partial<Record<UiTarget, boolean>>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);

  const view: UiView = `${size}.${screen}`;
  const phone = size === 'phone';
  const splash = screen === 'splash';
  const checks = uiContrastChecks(look.colours);
  const faint = checks.some((check) => !check.ok);
  const unsaved = !same(look, saved.look);

  function change(next: GameUiLook, edit = '') {
    setResult(null);
    const now = Date.now();
    const merge = edit !== '' && edit === lastEdit.current.edit && now - lastEdit.current.at < 1000;
    lastEdit.current = { edit, at: now };
    setHistory((h) => ({ past: merge ? h.past : [...h.past.slice(-99), look], future: [] }));
    setLook(next);
  }
  function step(back: boolean) {
    const from = back ? history.past : history.future;
    const to = from[from.length - 1];
    if (!to) return;
    lastEdit.current = { edit: '', at: 0 };
    setHistory(
      back
        ? { past: history.past.slice(0, -1), future: [...history.future, look] }
        : { past: [...history.past, look], future: history.future.slice(0, -1) },
    );
    setLook(to);
  }
  function set<K extends UiKey>(key: K, value: UiValues[K] | undefined) {
    change(withValue(look, view, target, key, value), `${view}/${target}/${key}`);
  }

  function goTo(nextSize: UiSize, nextScreen: UiScreen) {
    setSize(nextSize);
    setScreen(nextScreen);
    if (nextScreen === 'splash') {
      if (target === 'pause') setTarget('leaderboard');
      if (shown === 'pause') setShown('leaderboard');
    }
  }
  function pick(next: UiTarget) {
    setTarget(next);
    if (next !== 'theme') setShown(next);
    setMode('settings');
  }

  async function save() {
    setBusy(true);
    setResult(null);
    try {
      const answer = await apiSend<SavedLook>('PUT', `/v1/dev/projects/${project.id}/game-ui`, {
        look,
      });
      setSaved({ look: answer.look, at: answer.updatedAt, ever: true });
      setResult({
        tone: 'success',
        text: 'Saved. Testers see it the next time a panel opens; live players get it with your next published version.',
      });
    } catch (error) {
      setResult({
        tone: 'error',
        text: error instanceof ApiError ? error.message : 'Couldn’t save. Try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  const control = (key: UiKey) => {
    const r = resolveValue(look.views, view, target, key);
    const id = `${view}/${target}/${key}`;
    if (key === 'corners') {
      const sheet = phone && SHEETS.includes(target);
      const help = sheet
        ? 'On a phone it slides up from the bottom, so its bottom corners stay square.'
        : target === 'theme' && phone
          ? 'Panels that slide up from the bottom of a phone keep square bottom corners.'
          : undefined;
      return (
        <CornersSetting
          key={id}
          value={r.value as UiCorners}
          size={size}
          source={sourceOf(r, view, target, 'Corners')}
          linked={!unlinked[target]}
          onLink={(linked) => setUnlinked((u) => ({ ...u, [target]: !linked }))}
          squareBottom={sheet}
          help={help}
          onChange={(value) => set('corners', value)}
        />
      );
    }
    if (isChoice(key)) {
      const spec = CHOICE_SPECS[key];
      return (
        <ChoiceSetting
          key={id}
          label={spec.label}
          value={r.value as string}
          options={spec.options as [string, string][]}
          size={size}
          source={sourceOf(r, view, target, spec.label)}
          onChange={(value) => set(key, value as UiValues[typeof key] | undefined)}
        />
      );
    }
    const name = key as Exclude<UiKey, 'corners' | keyof typeof CHOICE_SPECS>;
    return (
      <NumberSetting
        key={id}
        name={name}
        value={r.value as number}
        size={size}
        source={sourceOf(r, view, target, NUMBER_SPECS[name].label)}
        onChange={(value) => set(name, value)}
      />
    );
  };

  const statusText = unsaved
    ? 'Changes not saved'
    : saved.ever && saved.at
      ? `Saved ${timeAgo(saved.at)}`
      : 'Using MOGZO’s look';
  const path = viewChain(view).slice().reverse();
  const targets = UI_TARGETS.filter((t) => !(splash && t === 'pause'));
  const darkButton =
    'grid size-[34px] place-items-center rounded-md text-[#a7a1c2] hover:bg-white/10 hover:text-white disabled:text-[#5d5679] disabled:hover:bg-transparent';

  return (
    <div className="flex h-screen min-h-[640px] flex-col bg-[#e9e7ef] text-[13px] text-ink">
      <header className="flex min-h-[52px] flex-none flex-wrap items-center gap-3 bg-[#17132b] px-3 py-2 text-white">
        <div className="flex min-w-0 flex-[1_1_260px] items-center gap-2.5">
          <Link
            to={`/projects/${project.id}`}
            aria-label={`Back to ${project.name}`}
            title={`Back to ${project.name}`}
            className="grid size-[34px] place-items-center rounded-md text-white hover:bg-white/10"
          >
            <Svg>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </Svg>
          </Link>
          <span className="font-display text-xl font-bold">mogzo</span>
          <span aria-hidden="true" className="h-[22px] w-px bg-[#2f2950]" />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-[11px] text-[#a7a1c2]">{project.name}</span>
            <h1 className="text-sm font-bold">Game UI</h1>
          </span>
        </div>

        <div className="flex items-center gap-2.5">
          <fieldset className="m-0 flex min-w-0 gap-0.5 rounded-lg border-0 bg-[#241e3f] p-[3px]">
            <legend className="sr-only">Screen size</legend>
            {UI_SIZES.map((s) => {
              const on = s === size;
              const n = countSet(look.views, `${s}.splash`) + countSet(look.views, `${s}.game`);
              const label = `${SIZE_NAMES[s]}${n ? `, ${n} ${n === 1 ? 'value' : 'values'} set` : ''}`;
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={on}
                  aria-label={label}
                  title={label}
                  onClick={() => goTo(s, screen)}
                  className={`relative grid h-8 w-10 place-items-center rounded-md ${
                    on ? 'bg-white text-[#17132b]' : 'text-[#c9c4dd] hover:text-white'
                  }`}
                >
                  <DeviceIcon size={s} />
                  {n > 0 && (
                    <span className="absolute top-0.5 right-0.5 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-[#6d4aff] px-[3px] text-[10px] font-bold text-white">
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </fieldset>
          <fieldset className="m-0 flex min-w-0 gap-0.5 rounded-lg border-0 bg-[#241e3f] p-[3px]">
            <legend className="sr-only">Screen</legend>
            {(['splash', 'game'] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={s === screen}
                onClick={() => goTo(size, s)}
                className={`h-8 rounded-md px-3.5 text-[13px] font-semibold ${
                  s === screen ? 'bg-white text-[#17132b]' : 'text-[#c9c4dd] hover:text-white'
                }`}
              >
                {s === 'splash' ? 'Splash' : 'Game'}
              </button>
            ))}
          </fieldset>
        </div>

        <div className="flex min-w-0 flex-[1_1_260px] items-center justify-end gap-1.5">
          <span aria-live="polite" className="mr-1 truncate text-xs text-[#a7a1c2]">
            {statusText}
          </span>
          <button
            type="button"
            aria-label="Undo"
            title="Undo"
            disabled={!history.past.length}
            onClick={() => step(true)}
            className={darkButton}
          >
            <Svg size={17}>
              <path d="M3 7v6h6" />
              <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
            </Svg>
          </button>
          <button
            type="button"
            aria-label="Redo"
            title="Redo"
            disabled={!history.future.length}
            onClick={() => step(false)}
            className={darkButton}
          >
            <Svg size={17}>
              <path d="M21 7v6h-6" />
              <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
            </Svg>
          </button>
          <button
            type="button"
            onClick={() => change(DEFAULT_GAME_UI)}
            disabled={same(look, DEFAULT_GAME_UI)}
            className="h-[34px] rounded-md border border-[#3a3359] px-3 font-semibold text-white hover:bg-white/10 disabled:text-[#5d5679] disabled:hover:bg-transparent"
          >
            Reset to MOGZO’s
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || faint || !unsaved}
            title={
              faint
                ? 'Some text is too faint to read. Fix it in Theme’s colours to save.'
                : undefined
            }
            className="h-[34px] rounded-md bg-brand px-[18px] font-bold text-ink hover:bg-brand-hover disabled:bg-[#4a4463] disabled:text-[#a7a1c2]"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>
      {result && (
        <p
          role={result.tone === 'error' ? 'alert' : 'status'}
          className={`flex-none px-4 py-2 text-[13px] font-semibold ${
            result.tone === 'error' ? 'bg-[#fde3e1] text-[#8a1c1c]' : 'bg-[#dcf3e3] text-[#1f5130]'
          }`}
        >
          {result.text}
        </p>
      )}

      <div className="flex min-h-0 flex-1">
        <aside
          aria-label="Settings"
          className="flex min-h-0 w-[340px] flex-none flex-col border-r border-[#dddbe5] bg-white"
        >
          {mode === 'settings' ? (
            <>
              <div className="flex h-12 flex-none items-center gap-1 border-b border-[#ececf1] px-2">
                <button
                  type="button"
                  aria-label="All elements"
                  title="All elements"
                  onClick={() => setMode('elements')}
                  className="grid size-8 place-items-center rounded-md text-[#3a3550] hover:bg-[#f3f2f7]"
                >
                  <Svg size={17}>
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </Svg>
                </button>
                <h2 className="min-w-0 flex-1 text-[15px] font-bold">{TARGET_NAMES[target]}</h2>
              </div>
              <div className="flex flex-none items-center gap-2 border-b border-[#ebe6ff] bg-[#f6f4ff] px-4 py-2 text-xs text-[#4a2fb8]">
                <span className="font-bold">Editing {viewName(view)}</span>
                <span className="text-muted">
                  {target === 'theme' ? 'Every panel starts here.' : 'Only this panel.'}
                </span>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {sectionsFor(target, phone).map((section) => {
                  const key = `${target}.${section.id}`;
                  const open = !closed[key];
                  const hasOwn = section.keys.some((k) => {
                    const r = resolveValue(look.views, view, target, k);
                    return r.view === view && r.target === target;
                  });
                  return (
                    <section key={key} className="border-b border-[#ececf1]">
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setClosed((c) => ({ ...c, [key]: open }))}
                        className="flex h-11 w-full items-center gap-2 bg-white px-4 text-left text-[13px] font-bold"
                      >
                        <span className="flex-1">{section.title}</span>
                        {hasOwn && (
                          <span
                            title="Has values set in this view"
                            className="size-[7px] rounded-full bg-[#6d4aff]"
                          />
                        )}
                        <span
                          className={`flex-none text-[#8a859c] transition-transform ${open ? '' : '-rotate-90'}`}
                        >
                          <Svg size={16}>
                            <path d="m6 9 6 6 6-6" />
                          </Svg>
                        </span>
                      </button>
                      {open && (
                        <div className="pb-2.5">
                          {section.colours && (
                            <Colours
                              look={look}
                              checks={checks}
                              faint={faint}
                              onPreset={(id) => change(applyPreset(look, id))}
                              onColour={(k, value) =>
                                change(
                                  {
                                    ...look,
                                    colours: { ...look.colours, [k]: value },
                                    preset: 'custom',
                                  },
                                  `colour/${k}`,
                                )
                              }
                            />
                          )}
                          {section.font && (
                            <div className="flex flex-col gap-1.5 px-4 pt-1.5 pb-2">
                              <div className="flex items-center gap-1.5">
                                <label
                                  htmlFor="ui-font"
                                  className="text-[12.5px] font-medium text-[#3a3550]"
                                >
                                  Font
                                </label>
                                <span
                                  title="The same in every view"
                                  className="grid place-items-center text-[#8a859c]"
                                >
                                  <Svg size={12}>
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                  </Svg>
                                </span>
                                <span className="ml-auto text-[11px] text-[#8a859c]">
                                  Every view
                                </span>
                              </div>
                              <select
                                id="ui-font"
                                value={look.font}
                                onChange={(event) =>
                                  change({
                                    ...look,
                                    font: event.target.value as UiFont,
                                    preset: 'custom',
                                  })
                                }
                                className="h-[30px] rounded border border-[#d9d7e2] bg-white px-2 text-[12.5px]"
                              >
                                {UI_FONTS.map((font) => (
                                  <option key={font} value={font}>
                                    {UI_FONT_NAMES[font]}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          {section.keys.map(control)}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="flex h-12 flex-none items-center border-b border-[#ececf1] px-4">
                <h2 className="text-[15px] font-bold">Elements</h2>
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-4 py-3.5">
                <p className="text-xs leading-relaxed text-muted">
                  Pick what to style. Theme sets colours, font and the sizes every panel starts
                  from; each panel can then change its own.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {targets.map((t) => {
                    const n = countSet(look.views, view, t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => pick(t)}
                        className={`flex min-h-[92px] flex-col items-center justify-center gap-[5px] rounded-md border bg-white px-1.5 py-2.5 text-center hover:border-[#b9a8ff] ${
                          t === target ? 'border-[#6d4aff]' : 'border-[#e3e1ea]'
                        }`}
                      >
                        <span className="text-[#4a4463]">
                          <Svg size={22} width={1.8}>
                            {TARGET_ICONS[t]}
                          </Svg>
                        </span>
                        <span className="text-[12.5px] font-semibold">{TARGET_NAMES[t]}</span>
                        <span className={`text-[11px] ${n ? 'text-[#4a2fb8]' : 'text-[#8a859c]'}`}>
                          {n
                            ? `${n} set in this view`
                            : t === 'theme'
                              ? 'Colours, font, sizes'
                              : 'Uses Theme'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {splash && (
                  <p className="text-xs text-muted">
                    A splash has no pause menu, so it isn’t listed here.
                  </p>
                )}
              </div>
            </>
          )}
          <p className="flex-none border-t border-[#ececf1] bg-[#fafafc] px-4 py-2.5 text-[11.5px] leading-snug text-muted">
            Testers see changes when you save. Live players get them with your next published
            version.
          </p>
        </aside>

        <Stage
          look={look}
          size={size}
          screen={screen}
          shown={target === 'theme' ? shown : (target as UiPanel)}
          selected={target !== 'theme'}
          onPick={(panel) => (splash && panel === 'pause' ? undefined : pick(panel))}
          toolbar={
            <>
              <span className="mr-1 text-[#8a859c]">Takes values from</span>
              {path.map((v, i) => {
                const on = v === view;
                const n = countSet(look.views, v);
                const [s, sc] = v.split('.') as [UiSize, UiScreen];
                return (
                  <span key={v} className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() => goTo(s, sc)}
                      className={`inline-flex h-[26px] items-center rounded-[5px] border px-[9px] text-xs font-semibold ${
                        on
                          ? 'border-[#6d4aff] bg-[#6d4aff] text-white'
                          : 'border-[#e3e1ea] bg-white text-[#3a3550] hover:border-[#b9a8ff]'
                      }`}
                    >
                      {viewName(v)}
                      {on ? ' (editing)' : ''}
                      {n > 0 && (
                        <span
                          className={`ml-1.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-bold text-[#4a2fb8] ${
                            on ? 'bg-white' : 'bg-[#efeaff]'
                          }`}
                        >
                          {n}
                        </span>
                      )}
                    </button>
                    {i < path.length - 1 && (
                      <span className="text-[#b9b3cf]">
                        <Svg size={14}>
                          <path d="m9 18 6-6-6-6" />
                        </Svg>
                      </span>
                    )}
                  </span>
                );
              })}
            </>
          }
        />
      </div>
    </div>
  );
}

/** Theme's colours: presets, the seven colours and whether players can read them. */
function Colours({
  look,
  checks,
  faint,
  onPreset,
  onColour,
}: {
  look: GameUiLook;
  checks: ReturnType<typeof uiContrastChecks>;
  faint: boolean;
  onPreset: (id: (typeof UI_PRESET_IDS)[number]) => void;
  onColour: (key: keyof UiColours, value: string) => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-1.5 px-4 pt-0.5 pb-2.5">
        <span className="flex items-center gap-1.5 text-xs font-medium text-[#3a3550]">
          Start from
          <span className="ml-auto text-[11px] font-normal text-[#8a859c]">Every view</span>
        </span>
        <div className="grid grid-cols-4 gap-1.5">
          {UI_PRESET_IDS.map((id) => {
            const preset = UI_PRESETS[id];
            const on = look.preset === id;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={on}
                title={preset.name}
                onClick={() => onPreset(id)}
                className={`flex min-w-0 flex-col gap-1 rounded-[5px] border bg-white p-1 text-left ${
                  on ? 'border-[#6d4aff]' : 'border-[#e3e1ea] hover:border-[#b9a8ff]'
                }`}
              >
                <span aria-hidden="true" className="flex h-[22px] overflow-hidden rounded-[3px]">
                  <span style={{ background: preset.colours.panel }} className="flex-[2]" />
                  <span style={{ background: preset.colours.accent }} className="flex-1" />
                </span>
                <span className="truncate text-[11px] font-semibold text-[#3a3550]">
                  {preset.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col px-4">
        {COLOURS.map(([key, label]) => (
          <ColourRow
            key={key}
            label={label}
            value={look.colours[key]}
            onChange={(v) => onColour(key, v)}
          />
        ))}
        <div className="mt-1.5 flex flex-col gap-[5px] rounded-md border border-[#ececf1] bg-[#fafafc] p-2.5">
          <span className="text-xs font-bold">Can players read it?</span>
          {checks.map((check) => (
            <div key={check.key} className="flex items-center gap-2 text-xs text-[#3a3550]">
              <span className="flex-1">{check.label}</span>
              <span className="font-mono text-[11.5px]">{check.ratio.toFixed(1)} : 1</span>
              <span
                className={`rounded-[3px] px-[7px] py-px text-[11px] font-bold ${
                  check.ok ? 'bg-[#dcf3e3] text-[#1f5130]' : 'bg-[#fde3e1] text-[#8a1c1c]'
                }`}
              >
                {check.ok ? 'Passes' : 'Too faint'}
              </span>
            </div>
          ))}
          {faint && (
            <p className="mt-0.5 text-xs text-[#8a1c1c]">
              Text needs at least 4.5 : 1 against what’s behind it. Change the colours marked Too
              faint to save.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

function ColourRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [typed, setTyped] = useState(value);
  const [shown, setShown] = useState(value);
  // A preset, undo or reset elsewhere changes the colour: follow it.
  if (shown !== value) {
    setShown(value);
    setTyped(value);
  }
  return (
    <div className="flex min-h-[34px] items-center gap-2.5">
      <input
        type="color"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="size-6 flex-none cursor-pointer overflow-hidden rounded-full border border-[#d9d7e2] bg-white p-0 [&::-moz-color-swatch]:rounded-full [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
      />
      <span className="min-w-0 flex-1 text-[12.5px] text-[#3a3550]">{label}</span>
      <input
        aria-label={`${label}, as a hex code`}
        value={typed}
        maxLength={7}
        spellCheck={false}
        onChange={(event) => {
          const next = event.target.value.trim();
          setTyped(next);
          if (/^#[0-9a-f]{6}$/i.test(next)) onChange(next.toLowerCase());
        }}
        className="w-[74px] rounded border border-transparent bg-transparent px-1 py-0.5 text-right font-mono text-[11.5px] text-[#8a859c] uppercase hover:border-[#d9d7e2] focus:border-[#d9d7e2] focus:text-ink"
      />
    </div>
  );
}

/**
 * dev.mogzo.com/projects/:id/ui: the Game UI editor, full screen (M19), opened in its own tab
 * from a project's header. It keeps its own light look whatever the dashboard's theme (M25).
 */
export function ProjectUi() {
  const { id = '' } = useParams();
  useEffect(() => {
    applyTheme('light');
    return () => applyTheme(readTheme());
  }, []);
  const project = useApi<Project>(`/v1/dev/projects/${encodeURIComponent(id)}`);
  const look = useApi<SavedLook>(
    project.status === 'ready' ? `/v1/dev/projects/${project.data.id}/game-ui` : null,
  );
  const failed =
    project.status === 'error' ? project.error : look.status === 'error' ? look.error : null;

  if (project.status === 'ready' && look.status === 'ready') {
    return <Editor project={project.data} initial={look.data} />;
  }
  return (
    <main className="mx-auto flex max-w-xl flex-col gap-4 px-4 py-10">
      <Link
        to={project.status === 'ready' ? `/projects/${project.data.id}` : '/'}
        className="font-semibold"
      >
        ← Back
      </Link>
      {failed ? <ErrorNotice error={failed} /> : <Loading />}
    </main>
  );
}
